import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Plus, Copy, X, Check, Trash2, Eraser } from "lucide-react";

const STATUS_LABELS = {
  active: { text: "АКТИВНО", cls: "border-foreground/40" },
  used: { text: "ИСПОЛЬЗОВАНО", cls: "border-foreground/15 text-foreground/50" },
  expired: { text: "ИСТЕКЛО", cls: "border-foreground/15 text-foreground/50" },
  revoked: { text: "ОТОЗВАНО", cls: "border-destructive/40 text-destructive" },
};

export default function Invites() {
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  const load = () => api.get("/api/admin/invites").then((r) => setInvites(r.data));
  useEffect(() => { load(); }, []);

  const createInvite = async () => {
    setErr(""); setBusy(true);
    try { await api.post("/api/admin/invites"); await load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const revoke = async (inv) => {
    if (!window.confirm(`Отозвать активное приглашение от ${(inv.created_at || "").slice(0, 10)}?\nСтрока останется в истории со статусом ОТОЗВАНО.`)) return;
    try { await api.post(`/api/admin/invites/${inv.id}/revoke`); await load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const hardDelete = async (inv) => {
    if (!window.confirm(`Удалить запись из истории?\nЭто полностью убирает строку из таблицы (без следа).`)) return;
    try { await api.delete(`/api/admin/invites/${inv.id}`); await load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const cleanupHistory = async () => {
    const toRemove = invites.filter((i) => i.status === "used" || i.status === "expired" || i.status === "revoked");
    if (toRemove.length === 0) return;
    if (!window.confirm(`Удалить ${toRemove.length} запись(ей) (использованные / истёкшие / отозванные)?\nАктивные не трогаются.`)) return;
    try {
      await Promise.all(toRemove.map((i) => api.delete(`/api/admin/invites/${i.id}`)));
      await load();
    } catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const copy = async (inv) => {
    try {
      await navigator.clipboard.writeText(inv.invite_url);
      setCopiedId(inv.id);
      setTimeout(() => setCopiedId(null), 1600);
    } catch (e) {
      window.prompt("Скопируйте ссылку вручную:", inv.invite_url);
    }
  };

  const active = invites.filter((i) => i.status === "active").length;
  const inactive = invites.length - active;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono">ПРИГЛАШЕНИЯ ДЛЯ VIEWER&apos;ОВ</div>
          <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-2">
            {invites.length} <span className="text-foreground/40">всего, {active} активных</span>
          </h1>
          <p className="text-sm text-foreground/60 mt-3 max-w-2xl">
            Можно создавать сколько угодно ссылок одновременно. Каждая <strong>одноразовая</strong>, живёт 14&nbsp;дней. Отправляйте viewer&apos;у любым каналом (Telegram, e-mail, WhatsApp). Регистрация — только email в&nbsp;зоне <span className="mono">.ru</span>.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {inactive > 0 && (
            <button onClick={cleanupHistory} className="btn-ghost" data-testid="invite-cleanup-btn" title="Удалить все использованные / истёкшие / отозванные">
              <Eraser size={14} /> Очистить историю ({inactive})
            </button>
          )}
          <button onClick={createInvite} disabled={busy} className="btn-primary" data-testid="invite-create-btn">
            <Plus size={14} /> {busy ? "Создаём…" : "Новое приглашение"}
          </button>
        </div>
      </div>

      {err && <p className="text-destructive label-mono" data-testid="invites-error">{err}</p>}

      <div className="border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary border-b border-black/10">
            <tr className="label-mono">
              <th className="text-left px-4 py-3">Статус</th>
              <th className="text-left px-4 py-3">Создано</th>
              <th className="text-left px-4 py-3">Истекает</th>
              <th className="text-left px-4 py-3">Использовано</th>
              <th className="text-left px-4 py-3">Ссылка</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody data-testid="invites-table-body">
            {invites.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-foreground/50">
                Приглашений нет. Создайте первое.
              </td></tr>
            )}
            {invites.map((inv) => {
              const meta = STATUS_LABELS[inv.status] || STATUS_LABELS.expired;
              const isActive = inv.status === "active";
              return (
                <tr key={inv.id} className="border-b border-black/5" data-testid={`invite-row-${inv.id}`}>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 border label-mono text-xs ${meta.cls}`}>{meta.text}</span>
                  </td>
                  <td className="px-4 py-3 mono text-xs text-foreground/70">{(inv.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3 mono text-xs text-foreground/70">{(inv.expires_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-foreground/70">{inv.used_by_email || "—"}</td>
                  <td className="px-4 py-3 mono text-xs max-w-[420px] truncate" title={inv.invite_url}>{inv.invite_url}</td>
                  <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                    {isActive && (
                      <>
                        <button onClick={() => copy(inv)} className="hop-link text-xs" data-testid={`invite-copy-${inv.id}`}>
                          {copiedId === inv.id ? <><Check size={12} className="inline" /> Скопировано</> : <><Copy size={12} className="inline" /> Копировать</>}
                        </button>
                        <button onClick={() => revoke(inv)} className="hop-link text-xs text-destructive" data-testid={`invite-revoke-${inv.id}`}>
                          <X size={12} className="inline" /> Отозвать
                        </button>
                      </>
                    )}
                    {!isActive && (
                      <button onClick={() => hardDelete(inv)} className="hop-link text-xs text-destructive" data-testid={`invite-delete-${inv.id}`}>
                        <Trash2 size={12} className="inline" /> Удалить
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
